import { LocalizationProvider } from "@appkit4/react-api";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";

import { queryClient } from "../api/queryClient";
import { AuthProvider } from "../contexts/AuthProvider";
import { BypassAuthProvider } from "../contexts/BypassAuthProvider";
import { DateOverrideProvider } from "../contexts/DateOverrideProvider";
import { ThemeProvider } from "../contexts/ThemeProvider";
import { ToastProvider } from "../contexts/ToastProvider";

const isBypassSso = import.meta.env.VITE_BYPASS_SSO === "true";

/**
 * Composes all app-level providers. Order: Query → Router → Auth → Theme → Toast.
 * LocalizationProvider is required for AppKit4 components (e.g. Select).
 * When VITE_BYPASS_SSO=true the MSAL auth provider is replaced with a dev bypass.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const ActiveAuthProvider = isBypassSso ? BypassAuthProvider : AuthProvider;

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LocalizationProvider>
          <ActiveAuthProvider>
            <ThemeProvider>
              <ToastProvider>
                <DateOverrideProvider>{children}</DateOverrideProvider>
              </ToastProvider>
            </ThemeProvider>
          </ActiveAuthProvider>
        </LocalizationProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

